import { useEffect } from 'react'
import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'

import { Dialog } from 'frame/components'
import { useLocation } from 'react-router-dom'
import { useDrillDownModalOnClose } from 'utils'
import DrillDownRoutes from './routes'
import Contract from './contract/contract'
import Driver from './driver'
import Vehicle from './vehicle'
import Variations from './variations'
import ServiceBookings from './service-bookings'
import NoDataView from './no-data-view'

import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'

import { constants } from '../../module'
import { useEnabledFeatureToggleList } from 'features/settings'

import TabMenu from './tab-menu'

const DrillDownModal = ({
  agreementNo,
  data,
  fleetIds,
  fleetFlags,
  serviceBookingFlags,
  errorMessage,

  // func
  onFetch,
  onFetchDriver,
  onReset,
  onResetDriverFlags
}) => {

  const featureToggleList = useEnabledFeatureToggleList()
  const drillDownFbtTabFeatureToggle = featureToggleList.includes('DrillDownFbtTab')

  const mobileView = useMediaQuery('(max-width: 600px)')

  const location = useLocation()
  const matchUrl = `${constants.PATH_DRILLDOWN}/${agreementNo}`

  const isContractTab = location.pathname.replace(matchUrl, '') === '' || location.pathname.replace(matchUrl, '') === constants.PATH_CONTACT
  const isServiceBookingTab = location.pathname.includes(constants.PATH_SERVICE_BOOKINGS)
  const isVehicleTab = location.pathname.includes(constants.PATH_VEHICLE)
  const isFbtTab = location.pathname.includes(constants.PATH_FBT)
  const isDriverTab = location.pathname.includes(constants.PATH_DRIVER)
  const isVariationsTab = location.pathname.includes(constants.PATH_VARIATIONS)


  const onClose = useDrillDownModalOnClose()

  const onCloseHandler = () => {
    onClose()
    onResetDriverFlags() // clear driver flags
  }

  useEffect(() => {
    onReset() // reset flags
    onFetch(agreementNo, fleetIds)
    onFetchDriver(agreementNo, fleetIds)
  }, [agreementNo, fleetIds]) // eslint-disable-line

  const isLoading = fleetFlags.fetch === 'processing' && ! data

  const isSaving = fleetFlags.update === 'processing' || fleetFlags.deleteVariation === 'processing'
                  || serviceBookingFlags.addServiceBooking === 'processing' || serviceBookingFlags.cancelServiceBooking === 'processing' || serviceBookingFlags.completeServiceBooking === 'processing'

  const hasErrored = fleetFlags.fetch === 'error' || ( ! isLoading && ! data)

  const isNonTfmVehicle = agreementNo.includes('UNF')

  const title = data?.accountName && data?.registration
    ? `${data?.accountName} - ${data?.registration}`
    : ``

  const tabsConfig = [
    {
      label: 'Contract',
      path: `${matchUrl}${constants.PATH_CONTACT}`,
      visible: true,
    }, {
      label: 'Driver',
      path: `${matchUrl}${constants.PATH_DRIVER}`,
      visible: true,
    }, {
      label: 'Vehicle',
      path: `${matchUrl}${constants.PATH_VEHICLE}`,
      visible: true,
    }, {
      label: 'Variations',
      path: `${matchUrl}${constants.PATH_VARIATIONS}`,
      visible: !isNonTfmVehicle,
    }, {
      label: 'Service Bookings',
      path: `${matchUrl}${constants.PATH_SERVICE_BOOKINGS}`,
      visible: !isNonTfmVehicle,
    }, {
      label: 'FBT',
      path: `${matchUrl}${constants.PATH_FBT}`,
      visible: drillDownFbtTabFeatureToggle
    }
  ].filter(x => x.visible)

  return (
    <Dialog
      open
      size="md"
      headerMinHeight={126}
      aria-labelledby="TFS Fleet"
      title={title}
      loading={isLoading || isSaving}
      onClose={onCloseHandler}
      tabsConfig={tabsConfig}
      tabMenu={<TabMenu config={tabsConfig} />}
      sx={{
        height: '80%',
      }}>
      {data && (
        <Stack pb={2}>
          {isContractTab && <Contract />}
          {isDriverTab && <Driver />}
          {isVehicleTab && <Vehicle />}
          {isVariationsTab && <Variations />}
          {isServiceBookingTab && <ServiceBookings />}
          {isFbtTab && <DrillDownRoutes />}
          {!isContractTab && !isDriverTab && !isVehicleTab && !isVariationsTab && !isServiceBookingTab && !isFbtTab && (
            <DrillDownRoutes />
          )}
        </Stack>
      )}

      {hasErrored && (
        <Grid container>
          <Grid item xs={12}>
            <NoDataView errorMessage={errorMessage} agreementNo={agreementNo}/>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '@media (max-width: 600px)': {
                display: 'block',
              }
            }}>
            <Button
              fullWidth={mobileView}
              sx={{
                marginTop: 2,
                marginRight: 2,
                minWidth: 180,
                '@media (max-width: 600px)': {
                  minWidth: '100%',
                },
              }}
              variant="contained"
              onClick={onCloseHandler}>
              Close
            </Button>
          </Grid>
        </Grid>
      )}
    </Dialog>
  )
}

DrillDownModal.propTypes = {
  agreementNo: PropTypes.string,
  data: PropTypes.object,
  fleetIds: PropTypes.array,
  fleetFlags: PropTypes.object,
  serviceBookingFlags: PropTypes.object,
  errorMessage: PropTypes.string,
  // func
  onFetch: PropTypes.func,
  onFetchDriver: PropTypes.func,
  onReset: PropTypes.func,
  onResetDriverFlags: PropTypes.func,
}

export default DrillDownModal
